/**
 * Reusable UPI / Bank Transfer payment card.
 *
 * Renders the corporate UPI ID, bank details, QR image, and a proof-upload
 * form. Calls `POST /api/manual-payments/submit` with the appropriate `kind`
 * + `target_id` when the user finishes. Downstream flows (eligibility,
 * partner, investment) then poll their own target status.
 */
import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Copy, Loader2, Smartphone, ShieldCheck, Upload } from "lucide-react";
import { ManualPayments } from "@/lib/api";
import { useRecaptchaToken } from "@/lib/recaptcha";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const MAX_PROOF_MB = 5;

export default function UpiPaymentCard({
  kind, // "eligibility" | "partner" | "investment"
  targetId,
  amount,
  payerName,
  payerEmail,
  payerPhone,
  onSubmitted,
  testIdPrefix = "upi",
}) {
  const [coords, setCoords] = useState(null);
  const [proof, setProof] = useState(null);
  const [upiRef, setUpiRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copyTick, setCopyTick] = useState("");
  const executeRecaptcha = useRecaptchaToken();

  useEffect(() => {
    ManualPayments.coordinates().then(setCoords).catch(() => {
      setError("Payment details are temporarily unavailable. Please try Stripe.");
    });
  }, []);

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyTick(label);
      setTimeout(() => setCopyTick(""), 1400);
    } catch {
      /* ignore */
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_PROOF_MB * 1024 * 1024) {
      setError(`Proof exceeds the ${MAX_PROOF_MB} MB limit.`);
      e.target.value = "";
      return;
    }
    setProof(f);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!proof) {
      setError("Please upload your payment screenshot to submit.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      // reCAPTCHA is now a SOFT check on this endpoint — the backend will
      // still accept the submission if the token is missing (it just flags
      // the payment for extra admin scrutiny). This mirrors the fix for the
      // prod bug where Google's script wouldn't hydrate in some browsers and
      // blocked real customers from completing payment.
      let recaptcha_token = "";
      try {
        recaptcha_token = (await executeRecaptcha("manual-payment-submit")) || "";
      } catch (_e) {
        recaptcha_token = "";
      }
      const r = await ManualPayments.submit(
        {
          kind,
          target_id: targetId,
          amount,
          payer_name: payerName,
          payer_email: payerEmail,
          payer_phone: payerPhone,
          upi_reference: upiRef,
          recaptcha_token,
        },
        proof,
      );
      onSubmitted?.(r);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!coords) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-white/60">
        <Loader2 size={20} className="mx-auto animate-spin text-[#F0D788]" />
        <div className="mt-3 text-sm">Loading payment details…</div>
      </div>
    );
  }

  return (
    <div
      data-testid={`${testIdPrefix}-card`}
      className="rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#0e2a6b] via-[#1c3d92] to-[#0A1F44] p-6 md:p-8"
    >
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#F0D788]">
        UPI / Bank Transfer
      </div>
      <div className="mt-2 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <h3 className="font-serif text-2xl md:text-3xl">
          Pay ₹{inr(amount)} to Sahyog Finance
        </h3>
        <div className="text-xs text-white/60">
          Scan the QR or use the UPI ID · Bank of Baroda
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-[220px_1fr] gap-6 items-start">
        {/* QR */}
        <div className="rounded-2xl bg-white p-3 shadow-lg">
          <img
            src={coords.qr_image_url}
            alt="Sahyog UPI QR code"
            className="w-full h-auto"
            data-testid={`${testIdPrefix}-qr`}
          />
          <div className="mt-2 text-center text-[10px] text-[#0A1F44] font-semibold tracking-wider">
            SCAN TO PAY
          </div>
        </div>

        {/* Details */}
        <div className="space-y-2.5">
          <CopyRow label="UPI ID" value={coords.upi_id} onCopy={copy} tick={copyTick === "upi_id"} testId={`${testIdPrefix}-upi`} />
          <CopyRow label="Account name" value={coords.account_name} onCopy={copy} tick={copyTick === "account_name"} testId={`${testIdPrefix}-name`} />
          <CopyRow label="Account number" value={coords.account_number} onCopy={copy} tick={copyTick === "account_number"} testId={`${testIdPrefix}-acct`} />
          <CopyRow label="IFSC code" value={coords.ifsc_code} onCopy={copy} tick={copyTick === "ifsc_code"} testId={`${testIdPrefix}-ifsc`} />
          <CopyRow label="Bank" value={coords.bank_name} onCopy={copy} tick={copyTick === "bank_name"} testId={`${testIdPrefix}-bank`} />
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-[#F0D788]/30 bg-[#F0D788]/[0.06] p-4 text-xs text-[#F0D788] leading-relaxed">
        <strong>Step 1:</strong> Tap an app below OR scan the QR / use the UPI ID to pay{" "}
        <strong>₹{inr(amount)}</strong>.<br />
        <strong>Step 2:</strong> After payment, take a clear screenshot showing the transaction
        ID.<br />
        <strong>Step 3:</strong> Upload the screenshot below and submit for verification.<br />
        <em className="text-white/60">A unique reference ID is generated after you submit proof.</em>
      </div>

      {/* UPI deep-link launch buttons — opens the payer's UPI app (Android intent) or the
          generic UPI protocol handler (iOS). Amount + note are pre-filled from props. */}
      <UpiAppLauncher
        upiId={coords.upi_id}
        payeeName={coords.account_name}
        amount={amount}
        note={`Sahyog ${kind} · ${targetId?.slice(0, 8) || ""}`}
        testIdPrefix={testIdPrefix}
      />

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5 block">
            UPI / bank transaction reference (optional)
          </label>
          <input
            data-testid={`${testIdPrefix}-ref`}
            value={upiRef}
            onChange={(e) => setUpiRef(e.target.value)}
            placeholder="e.g. UPI/423012345678"
            className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/10 outline-none focus:border-[#D4AF37]/60 text-sm text-white placeholder:text-white/30"
          />
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5 block">
            Payment screenshot <span className="text-[#D4AF37]">*</span>
          </label>
          <label
            data-testid={`${testIdPrefix}-proof-label`}
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition ${
              proof
                ? "bg-emerald-500/[0.06] border-emerald-500/40"
                : "bg-white/[0.03] border-white/10 hover:border-[#D4AF37]/50"
            }`}
          >
            {proof ? (
              <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
            ) : (
              <Upload size={16} className="text-[#F0D788] shrink-0" />
            )}
            <div className="flex-1 text-sm">
              {proof ? (
                <>
                  <div className="text-white/85 truncate">{proof.name}</div>
                  <div className="text-[11px] text-white/50">{(proof.size / 1024).toFixed(0)} KB</div>
                </>
              ) : (
                <div className="text-white/75">Click to upload screenshot (JPG / PNG / PDF · max 5 MB)</div>
              )}
            </div>
            <input
              data-testid={`${testIdPrefix}-proof-input`}
              type="file"
              className="hidden"
              accept="image/jpeg,image/jpg,image/png,image/webp,.pdf"
              onChange={onFile}
            />
          </label>
        </div>

        {error && (
          <div
            data-testid={`${testIdPrefix}-error`}
            className="flex items-start gap-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm px-3 py-2.5"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid={`${testIdPrefix}-submit`}
          className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 size={15} className="animate-spin" /> Submitting…
            </>
          ) : (
            <>Submit payment proof for verification</>
          )}
        </button>

        <div className="text-[11px] text-white/40 flex items-start gap-1.5">
          <ShieldCheck size={11} className="mt-0.5 text-[#D4AF37] shrink-0" />
          <span>
            Your status will move to <strong>Pending Verification</strong> until our team
            confirms the payment (usually within 5 to 6 business days). You will be emailed once
            approved.
          </span>
        </div>
      </form>
    </div>
  );
}

function CopyRow({ label, value, onCopy, tick, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-black/25 border border-white/10 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</div>
        <div
          data-testid={testId}
          className="text-sm text-white/90 truncate font-mono"
          title={value}
        >
          {value}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onCopy(label.toLowerCase().replace(/\s+/g, "_"), value)}
        className="p-2 rounded-lg text-white/60 hover:text-[#F0D788] hover:bg-white/5 shrink-0"
        title="Copy"
      >
        {tick ? <CheckCircle2 size={14} className="text-emerald-400" /> : <Copy size={14} />}
      </button>
    </div>
  );
}

/**
 * UpiAppLauncher — opens Google Pay / PhonePe / Paytm / BHIM / any UPI app
 * with a pre-filled payment request via the standard `upi://pay?…` intent.
 *
 * On Android, tapping opens the UPI intent chooser directly. On iOS,
 * Apple restricts UPI intents in Safari — we fall back to opening the
 * app via its custom scheme when available (gpay://, phonepe://, paytmmp://).
 */
const UPI_APPS = [
  { key: "gpay",    label: "Google Pay", scheme: "tez://upi/pay",       accent: "bg-white text-[#0A1F44]" },
  { key: "phonepe", label: "PhonePe",    scheme: "phonepe://pay",       accent: "bg-[#5f259f] text-white" },
  { key: "paytm",   label: "Paytm",      scheme: "paytmmp://pay",       accent: "bg-[#00baf2] text-white" },
  { key: "bhim",    label: "BHIM",       scheme: "bhim://upi/pay",      accent: "bg-[#f47b20] text-white" },
];

function UpiAppLauncher({ upiId, payeeName, amount, note, testIdPrefix }) {
  const params = new URLSearchParams({
    pa: upiId,
    pn: payeeName || "Sahyog Finance",
    am: String(amount || ""),
    cu: "INR",
    tn: note || "Payment",
  });
  const genericUpi = `upi://pay?${params.toString()}`;

  const openApp = (scheme) => {
    // Try the app-specific scheme first — if not installed, browsers will
    // fall through to the generic upi:// handler / chooser after a short delay.
    const appUrl = `${scheme}?${params.toString()}`;
    const win = window.open(appUrl, "_self");
    // Fallback timer for browsers that silently fail app-scheme redirects.
    setTimeout(() => {
      try {
        if (!win || win.closed) window.location.href = genericUpi;
        else window.location.href = genericUpi;
      } catch {
        window.location.href = genericUpi;
      }
    }, 900);
  };

  return (
    <div
      data-testid={`${testIdPrefix}-app-launcher`}
      className="mt-5 rounded-2xl border border-[#D4AF37]/40 bg-black/25 p-4 md:p-5"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F0D788]">
        <Smartphone size={13} /> Pay via UPI app · one tap
      </div>
      <p className="mt-1.5 text-[11px] text-white/55">
        Tap your preferred app to open it with amount &amp; UPI ID pre-filled. Works best on mobile.
      </p>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2.5">
        {UPI_APPS.map((app) => (
          <button
            key={app.key}
            type="button"
            data-testid={`${testIdPrefix}-app-${app.key}`}
            onClick={() => openApp(app.scheme)}
            aria-label={`Pay ₹${amount} via ${app.label}`}
            className={`min-h-[44px] px-3 py-3 rounded-xl text-sm font-semibold ${app.accent} hover:brightness-110 transition inline-flex items-center justify-center gap-1.5`}
          >
            {app.label}
          </button>
        ))}
      </div>
      <a
        data-testid={`${testIdPrefix}-app-any`}
        href={genericUpi}
        className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-[#F0D788] hover:underline"
      >
        Or open any UPI app installed on this device →
      </a>
    </div>
  );
}
